import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Badge } from '../../components/Badge';
import { PageHeader } from '../../components/PageHeader';
import { Button } from '../../components/Button';
import { EmptyState } from '../../components/EmptyState';
import { ErrorNotice } from '../../components/ErrorNotice';
import { LoadingIndicator } from '../../components/Spinner';
import { formatDateTime } from '../../i18n';
import { PortalRequestForm } from './PortalRequestForm';
import { useCreateMyTicket, useMyTickets } from './queries';

/**
 * Die Vorgaenge eines Portalnutzers -- F4-18.
 *
 * Welche das sind, entscheidet der Server (F4-19): eigene Vorgaenge oder alle
 * des Kunden, je nach Rolle. Die Liste zeigt, was sie bekommt.
 *
 * **Kein Auswahlfeld fuer Warteschlange, Typ oder Prioritaet.** Ein Kunde
 * weiss nicht, in welche Warteschlange sein Anliegen gehoert -- und wenn er
 * es wuesste, waere die Angabe eine Vorgabe an die Bearbeitung, die diese
 * ohnehin pruefen muss. Betreff und Sachverhalt genuegen; alles Weitere setzt
 * der Server aus den Vorgaben des Mandanten.
 */
export function PortalTicketList({
  onOpen,
  onCreated,
}: {
  onOpen: (ticketId: string) => void;
  onCreated: () => void;
}) {
  const { t, i18n } = useTranslation();
  const tickets = useMyTickets();
  const create = useCreateMyTicket();

  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');

  return (
    <>
      <PageHeader title={t('portal.myTickets')} />

      {/* Anfragetypen zuerst -- F4-20. Wer einen passenden findet, gibt
          gezielt Auskunft; wer keinen findet, schreibt darunter frei. */}
      <PortalRequestForm onCreated={onCreated} />

      <section className="card">
        <h2>{t('portal.newRequest')}</h2>

        <div className="field">
          <label className="field__label" htmlFor="portal-title">
            {t('portal.subject')}
          </label>
          <input
            id="portal-title"
            className="field__control"
            value={title}
            placeholder={t('portal.subjectPlaceholder')}
            onChange={(event) => {
              setTitle(event.target.value);
            }}
          />
        </div>

        <div className="field">
          <label className="field__label" htmlFor="portal-description">
            {t('portal.description')}
          </label>
          <textarea
            id="portal-description"
            className="field__control field__control--area"
            rows={5}
            value={description}
            placeholder={t('portal.descriptionPlaceholder')}
            onChange={(event) => {
              setDescription(event.target.value);
            }}
          />
        </div>

        {create.isError ? <ErrorNotice error={create.error} /> : null}

        <Button
          variant="primary"
          disabled={title.trim().length < 3}
          busy={create.isPending}
          onClick={() => {
            create.mutate(
              { title: title.trim(), description },
              {
                onSuccess: () => {
                  setTitle('');
                  setDescription('');
                },
              },
            );
          }}
        >
          {t('portal.send')}
        </Button>
      </section>

      <section className="card card--flush">
        {tickets.isPending ? <LoadingIndicator /> : null}
        {tickets.isError ? <ErrorNotice error={tickets.error} /> : null}

        {tickets.data?.items.length === 0 ? (
          <EmptyState
            icon="tickets"
            title={t('portal.noTickets')}
            hint={t('portal.noTicketsHint')}
          />
        ) : null}

        {/* Dieselbe Trefferkarte wie in der Wissenssuche. Vorher trug das
            Listenelement ``history__change`` -- einen ``display:
            contents``-Helfer, der es aufloest: Titel, Stand und Nummer
            wurden je zu einem eigenen Streifen ueber die volle Breite, und
            welcher Stand zu welchem Vorgang gehoerte, war nicht mehr zu
            sehen. */}
        <ul className="result-list result-list--plain">
          {(tickets.data?.items ?? []).map((ticket) => (
            <li key={ticket.id} className="result">
              <button
                type="button"
                className="result__title"
                onClick={() => {
                  onOpen(ticket.id);
                }}
              >
                {ticket.title}
              </button>
              <span className="result__meta">
                <span className="result__fact">
                  <Badge tone="info" dot>
                    {ticket.state.name}
                  </Badge>
                </span>
                <span className="result__fact">{ticket.ticket_number}</span>
                <span className="result__fact">
                  {t('portal.updatedAt', {
                    moment: formatDateTime(ticket.updated_at, i18n.language),
                  })}
                </span>
              </span>
            </li>
          ))}
        </ul>
      </section>
    </>
  );
}
